import React, { useState } from 'react';
import { Package, MagnifyingGlass, Ticket, CheckCircle } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND, useCapabilities } from '_helpers';
import { PageHeader, Bezel, Button, Input, Select, Tag, LoadingScreen } from '_ui';

// Sell a session bundle and redeem sessions from it. Both roles. Selling books revenue (no
// commission); redeeming credits the PERFORMER the bundle's per-session commission.
//   - employee redeems  -> credited to themselves.
//   - manager redeems   -> must pick which employee performed it.

export function BanGoi() {
    const caps = useCapabilities();
    const { data: types, loading } = useFetch('/api/package-types');

    if (loading) return <LoadingScreen />;

    return (
        <div className="space-y-10">
            <PageHeader eyebrow="Vận hành" title="Bán gói" description="Bán gói nhiều buổi cho khách, và trừ buổi khi khách tới dùng." />
            <div className="grid gap-8 lg:grid-cols-2">
                <SellCard types={types || []} />
                <RedeemCard isManager={!!caps.canManagePackages} />
            </div>
        </div>
    );
}

function SellCard({ types }) {
    const api = useApi();
    const alert = useAlertActions();
    const [typeId, setTypeId] = useState('');
    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);

    const options = types.map(t => ({ value: t._id, label: `${t.name} · ${t.sessionCount} buổi · ${formatVND(t.price)}` }));

    async function sell() {
        if (!typeId) { alert.error('Chưa chọn gói', 'Hãy chọn loại gói.'); return; }
        if (!phone.trim()) { alert.error('Thiếu số điện thoại', 'Nhập số điện thoại khách để tra cứu sau này.'); return; }
        setSaving(true);
        const res = await api.post('/api/packages', { packageTypeId: typeId, customerPhone: phone.trim(), customerName: name.trim() });
        setSaving(false);
        if (res.ok) {
            alert.success('Đã bán gói', `${res.data.sessionsRemaining} buổi cho ${phone.trim()}.`);
            setTypeId(''); setPhone(''); setName('');
        } else alert.error('Không bán được gói', res.error);
    }

    return (
        <Bezel className="space-y-5 p-6 sm:p-8">
            <div className="flex items-center gap-2 text-espresso"><Package size={22} weight="light" /><h3 className="font-serif text-2xl">Bán gói mới</h3></div>
            {types.length === 0
                ? <p className="text-sm text-espresso-soft/70">Chưa có gói nào đang bán. Quản lý tạo gói ở trang “Gói dịch vụ”.</p>
                : <>
                    <Select label="Loại gói" value={typeId} onChange={setTypeId} options={options} placeholder="Chọn gói" />
                    <Input label="Số điện thoại khách" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="VD: 0901234567" />
                    <Input label="Tên khách (tuỳ chọn)" value={name} onChange={e => setName(e.target.value)} placeholder="VD: Chị Lan" />
                    <Button onClick={sell} loading={saving} className="w-full justify-center">{saving ? 'Đang bán…' : 'Bán gói'}</Button>
                </>}
        </Bezel>
    );
}

function RedeemCard({ isManager }) {
    const api = useApi();
    const alert = useAlertActions();
    const [phone, setPhone] = useState('');
    const [pkgs, setPkgs] = useState(null); // null = not searched yet
    const [searching, setSearching] = useState(false);
    const [performerId, setPerformerId] = useState('');
    const [busyId, setBusyId] = useState('');

    async function search() {
        if (!phone.trim()) { alert.error('Nhập số điện thoại', 'Nhập số điện thoại khách để tra cứu.'); return; }
        setSearching(true);
        const res = await api.get(`/api/packages/lookup?phone=${encodeURIComponent(phone.trim())}`);
        setSearching(false);
        if (res.ok) setPkgs(res.data);
        else { setPkgs(null); alert.error('Không tra cứu được', res.error); }
    }

    async function redeem(pkg) {
        if (isManager && !performerId) { alert.error('Chưa chọn nhân viên', 'Chọn nhân viên thực hiện buổi này.'); return; }
        setBusyId(pkg._id);
        const res = await api.post(`/api/packages/${pkg._id}/redeem`, isManager ? { performerId } : {});
        setBusyId('');
        if (res.ok) {
            const remaining = res.data.package.sessionsRemaining;
            alert.success('Đã trừ 1 buổi', `Còn lại ${remaining} buổi.`);
            setPkgs(prev => prev.map(p => (p._id === pkg._id ? res.data.package : p)).filter(p => p.sessionsRemaining > 0));
        } else alert.error('Không trừ được buổi', res.error);
    }

    return (
        <Bezel className="space-y-5 p-6 sm:p-8">
            <div className="flex items-center gap-2 text-espresso"><Ticket size={22} weight="light" /><h3 className="font-serif text-2xl">Dùng buổi</h3></div>
            <div className="flex items-end gap-3">
                <Input label="Số điện thoại khách" type="tel" value={phone} className="flex-1"
                    onChange={e => setPhone(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} placeholder="VD: 0901234567" />
                <Button icon={MagnifyingGlass} onClick={search} loading={searching} className="mb-[1px]">Tra cứu</Button>
            </div>

            {isManager && <PerformerPicker value={performerId} onChange={setPerformerId} />}

            {pkgs !== null && pkgs.length === 0 && (
                <p className="text-sm text-espresso-soft/70">Không tìm thấy gói còn buổi cho số này.</p>
            )}
            <div className="space-y-3">
                {(pkgs || []).map(p => (
                    <div key={p._id} className="rounded-2xl bg-cream-200/50 p-4 ring-1 ring-espresso/10">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="font-medium text-espresso">{p.packageName}</div>
                                <div className="text-xs text-espresso-soft/70">{p.serviceName} · HH/buổi {formatVND(p.perSessionCommission)}</div>
                                {p.customerName ? <div className="text-xs text-espresso-soft/70">{p.customerName}</div> : null}
                            </div>
                            <Tag tone="sage">Còn {p.sessionsRemaining}/{p.sessionsTotal}</Tag>
                        </div>
                        <Button icon={CheckCircle} onClick={() => redeem(p)} loading={busyId === p._id} size="sm" className="mt-3 w-full justify-center">Dùng 1 buổi</Button>
                    </div>
                ))}
            </div>
        </Bezel>
    );
}

// Manager-only: pick which employee performed the session (only rendered for managers, so employees
// never hit the manager-only /api/employees endpoint).
function PerformerPicker({ value, onChange }) {
    const { data: employees } = useFetch('/api/employees');
    const options = (employees || []).filter(e => e.active).map(e => ({ value: e._id, label: e.name }));
    return <Select label="Nhân viên thực hiện" value={value} onChange={onChange} options={options} placeholder="Chọn nhân viên" />;
}
